import { connectorIsReady } from "@/lib/channel-coverage";
import { publicationStateMeta } from "@/lib/listing-pricing";
import {
  bigcommerceAdminProductUrl,
  bigcommerceStorefrontProductUrl,
  ebayLiveItemUrl,
  parsePublicHttpsOrigin,
  parseStorefrontProductPath,
} from "@/lib/marketplace-live-links";

export function indexConnectorStorefront(
  rows: Array<{ connector_id: string; storefront_origin?: string | null; store_hash?: string | null }>,
): { originByConnector: Record<string, string>; storeHashByConnector: Record<string, string> } {
  const originByConnector: Record<string, string> = {};
  const storeHashByConnector: Record<string, string> = {};
  for (const row of rows) {
    const origin = parsePublicHttpsOrigin(row.storefront_origin);
    if (origin) originByConnector[row.connector_id] = origin;
    const hash = String(row.store_hash ?? "").trim();
    if (/^[a-z0-9]{6,20}$/i.test(hash)) storeHashByConnector[row.connector_id] = hash;
  }
  return { originByConnector, storeHashByConnector };
}

export function indexBcListingStorefrontPaths(
  rows: Array<{ listing_id: string; storefront_custom_url?: string | null }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const path = parseStorefrontProductPath(row.storefront_custom_url);
    if (path) out[row.listing_id] = path;
  }
  return out;
}

export function listingIsProviderConfirmed(input: {
  provider?: string | null;
  externalListingId?: string | null;
  providerConfirmedId?: string | null;
  publicationStatus?: string | null;
}): boolean {
  if (input.providerConfirmedId) return true;
  if (input.publicationStatus === "published" && input.externalListingId) return true;
  if (input.provider === "bigcommerce" && input.externalListingId) return true;
  if (input.provider === "ebay" && /^\d{9,20}$/.test(String(input.externalListingId ?? ""))) return true;
  return Boolean(input.providerConfirmedId);
}

export function listingPublicationInput(listing: {
  publication_status?: string | null;
  status?: string | null;
  dirty_domains?: string[] | null;
  last_error?: string | null;
  external_listing_id?: string | null;
  connector?: { provider?: string | null; auth_status?: string | null };
  provider_confirmed_id?: string | null;
}) {
  const provider = listing.connector?.provider;
  const confirmedId = listing.provider_confirmed_id || (provider === "bigcommerce" ? listing.external_listing_id : null);
  return {
    publicationStatus: listing.publication_status as never,
    status: listing.status as never,
    hasDirty: (listing.dirty_domains?.length ?? 0) > 0,
    dirtyDomains: listing.dirty_domains ?? [],
    connectionReady: connectorIsReady(listing.connector?.auth_status),
    lastError: listing.last_error,
    providerConfirmedListed: listingIsProviderConfirmed({
      provider,
      externalListingId: listing.external_listing_id,
      providerConfirmedId: confirmedId,
      publicationStatus: listing.publication_status,
    }),
  };
}

export function listingLiveLinks(opts: {
  provider?: string | null;
  externalId?: string | null;
  storefrontOrigin?: string | null;
  storeHash?: string | null;
  customUrl?: string | null;
}): { liveUrl: string | null; adminUrl: string | null } {
  const provider = opts.provider ?? "";
  if (provider === "ebay") {
    return { liveUrl: ebayLiveItemUrl(opts.externalId), adminUrl: null };
  }
  if (provider === "bigcommerce") {
    return {
      liveUrl: bigcommerceStorefrontProductUrl({
        storefrontOrigin: opts.storefrontOrigin,
        customUrl: opts.customUrl,
        productId: opts.externalId,
      }),
      adminUrl: bigcommerceAdminProductUrl({ storeHash: opts.storeHash, productId: opts.externalId }),
    };
  }
  return { liveUrl: null, adminUrl: null };
}

export function listingStateMeta(listing: Parameters<typeof listingPublicationInput>[0]) {
  return publicationStateMeta(listingPublicationInput(listing));
}
